import type { OrderData, PagoParcial } from '../types/orderFormTypes'

/** Plan Separe en abonado: se pueden agregar/editar abonos no verificados. */
export const ORDER_STATUSES_EDITABLE_FOR_PAYMENT = [
  'pending',
  'verify_transaction',
  'abonado'
] as const

export type OrderStatusEditableForPayment = (typeof ORDER_STATUSES_EDITABLE_FOR_PAYMENT)[number]

export function normalizeOrderStatus(status: string | undefined): string {
  return (status || '').toString().trim().toLowerCase()
}

export function isOrderStatusEditableForPayment(status: string | undefined): boolean {
  const s = normalizeOrderStatus(status)
  return ORDER_STATUSES_EDITABLE_FOR_PAYMENT.includes(s as OrderStatusEditableForPayment)
}

export function isPagoVerificado(value: unknown): boolean {
  return Number(value) === 1
}

export function getOrderPagoVerificado(formData: OrderData): boolean {
  const root = (formData as { pago_verificado?: unknown }).pago_verificado
  if (root !== undefined && root !== null) {
    return isPagoVerificado(root)
  }
  return false
}

export function isPartOrPlanSeparePaymentMethod(paymentMethod: string | undefined): boolean {
  const pm = (paymentMethod || '').toString().trim().toLowerCase()
  return pm === 'part' || pm === 'plan_separe'
}

export function hasAnyPagoParcialVerificado(pagos: PagoParcial[] | undefined): boolean {
  if (!Array.isArray(pagos) || pagos.length === 0) return false
  return pagos.some((p) => isPagoVerificado(p.pago_verificado))
}

export function canEditMainPaymentMethod(
  mode: 'view' | 'edit' | 'create',
  status: string | undefined,
  options?: {
    paymentMethod?: string
    pagosParciales?: PagoParcial[]
  }
): boolean {
  if (mode === 'create') return true
  if (mode === 'view') return false
  if (!isOrderStatusEditableForPayment(status)) return false

  const pm = options?.paymentMethod
  if (
    isPartOrPlanSeparePaymentMethod(pm) &&
    hasAnyPagoParcialVerificado(options?.pagosParciales)
  ) {
    return false
  }

  return true
}

export function getMainPaymentMethodEditBlockReason(
  mode: 'view' | 'edit' | 'create',
  status: string | undefined,
  options?: {
    paymentMethod?: string
    pagosParciales?: PagoParcial[]
  }
): string | undefined {
  if (canEditMainPaymentMethod(mode, status, options)) return undefined
  if (mode !== 'edit') return undefined

  if (!isOrderStatusEditableForPayment(status)) {
    return 'Solo se puede modificar en estado Pendiente, Verificar transacción o Abonado'
  }

  if (
    isPartOrPlanSeparePaymentMethod(options?.paymentMethod) &&
    hasAnyPagoParcialVerificado(options?.pagosParciales)
  ) {
    return 'No se puede cambiar: ya hay al menos un pago o abono verificado'
  }

  return undefined
}

/**
 * Fila de abono/pago parcial bloqueada para edición/eliminar.
 * - Verificado (pago_verificado = 1): nunca editable.
 * - No verificado: editable solo en pending, verify_transaction o abonado.
 * - Nuevos y existentes no verificados siguen la misma regla.
 */
export function isPagoParcialRowLocked(
  mode: 'view' | 'edit' | 'create',
  status: string | undefined,
  pago: PagoParcial
): boolean {
  if (mode === 'view') return true
  if (mode === 'create') return false
  if (isPagoVerificado(pago.pago_verificado)) return true
  if (!isOrderStatusEditableForPayment(status)) return true
  return false
}

/** Permite el botón "+ Agregar abono/pago" en edición. */
export function canAgregarPagoParcial(
  mode: 'view' | 'edit' | 'create',
  status: string | undefined
): boolean {
  if (mode === 'view') return false
  if (mode === 'create') return true
  return isOrderStatusEditableForPayment(status)
}

export function canShowBoldRegenerateButton(
  mode: 'view' | 'edit' | 'create',
  pagoVerificado: boolean,
  status?: string
): boolean {
  if (mode !== 'edit') return false
  if (pagoVerificado) return false
  if (!isOrderStatusEditableForPayment(status)) return false
  return true
}

export function shouldShowBoldCheckoutLink(status: string | undefined): boolean {
  const s = normalizeOrderStatus(status)
  return s === 'pending' || s === 'processing' || s === 'verify_transaction'
}
